import studentService from 'ApiService/Teacher/Student';
import testService from 'ApiService/Teacher/TestService';
import Card from 'components/card';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { UseContextProvider } from 'views/admin/main/users/newUser/contexts/StepperContext';
import SelectClass from './Class/Index';
import AttendancesSheet from './Sheet/Index';
import BulkSheet from './BulkSheet/Index';
import attendanceService from 'ApiService/Teacher/Attendance';
import CourseCompleteModal from './Components/CourseCompleteModal';
import { toLocalDateStr } from 'utils/date';

const MarkAttendance = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [classData, setClassData] = useState({
    courseId: '',
    structureId: '',
    batchId: '',
  });

  const [attendance, setAttendance] = useState([]);
  const [disable, setDisable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [date, setDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lectureData, setLectureData] = useState(null);
  const [selectedSubtopics, setSelectedSubtopics] = useState([]);

  const [completedStudents, setCompletedStudents] = useState([]);
  const [batchCourses, setBatchCourses] = useState([]);
  // Earliest selectable attendance date: the batch start, or earlier when
  // students were merged in from an older batch.
  const [attendanceStartDate, setAttendanceStartDate] = useState(null);
  const [completedCourses, setCompletedCourses] = useState([]);
  const [showCourseCompleteModal, setShowCourseCompleteModal] = useState(false);
  const [attendanceMode, setAttendanceMode] = useState('single');

  const applyCompletedCoursesToAttendance = (
    rows = [],
    courseList = completedCourses
  ) => {
    const completedCourseIds = new Set(
      (courseList || [])
        .map((courseInfo) => courseInfo?.course?.toString())
        .filter(Boolean)
    );

    return (rows || []).map((student) => {
      const isCompletedNow =
        !!student.enrolledCourseId &&
        completedCourseIds.has(student.enrolledCourseId.toString());

      return {
        ...student,
        isCompleted: isCompletedNow,
        isPresent: isCompletedNow ? true : student.isPresent,
      };
    });
  };

  useEffect(() => {
    setAttendance((prevAttendance) => {
      if (!prevAttendance.length) {
        setCompletedStudents([]);
        return prevAttendance;
      }

      const nextAttendance = applyCompletedCoursesToAttendance(
        prevAttendance,
        completedCourses
      );
      setCompletedStudents(
        nextAttendance.filter((student) => student.isCompleted)
      );
      return nextAttendance;
    });
  }, [completedCourses]);

  const steps = [
    { stepNo: 1, name: 'Class Info' },
    { stepNo: 2, name: 'Add Details' },
  ];

  const cycleAttendanceValue = (current) => {
    if (current === null) return true;
    if (current === true) return false;
    return null;
  };

  const handleCheckboxChange = (studentId) => {
    setAttendance((prevAttendance) => {
      return prevAttendance.map((student) => {
        if (student.id === studentId) {
          return { ...student, isPresent: cycleAttendanceValue(student.isPresent) };
        }
        return student;
      });
    });
  };

  const getAttendanceMarkAllState = () => {
    const markable = attendance.filter((s) => !s.isCompleted);
    if (markable.length === 0) return 'mixed';
    const values = markable.map((s) => s.isPresent);
    if (values.every((v) => v === null)) return 'null';
    if (values.every((v) => v === true)) return 'true';
    if (values.every((v) => v === false)) return 'false';
    return 'mixed';
  };

  const handleMarkAllAttendance = () => {
    const state = getAttendanceMarkAllState();
    let nextValue;
    if (state === 'null') nextValue = true;
    else if (state === 'true') nextValue = false;
    else if (state === 'false') nextValue = null;
    else nextValue = null;

    setAttendance((prevAttendance) =>
      prevAttendance.map((student) =>
        student.isCompleted ? student : { ...student, isPresent: nextValue }
      )
    );
  };

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const response = await testService.getAllCourses();
      if (response.data.success) {
        setCourses(response.data.courses);
      } else {
        toast.error('Failed to fetch courses.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch courses.');
    } finally {
      setLoadingCourses(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchBatchCourses = async () => {
    if (!classData.batchId) return;
    setAttendanceStartDate(null);
    try {
      const response = await studentService.getBatchCourses(classData.batchId);
      if (response.data.success) {
        setBatchCourses(response.data.courses || []);
        setCompletedCourses(response.data.completedCourses || []);
        setAttendanceStartDate(response.data.attendanceStartDate || null);
      }
    } catch (error) {
      console.error('Error fetching batch courses:', error);
    }
  };

  useEffect(() => {
    if (classData.batchId) {
      fetchBatchCourses();
    }
  }, [classData.batchId]);

  async function getStudents() {
    try {
      const formattedDate = toLocalDateStr(date);
      const response = await studentService.getAllStudentForAttendance(
        classData.courseId,
        classData.batchId,
        formattedDate
      );
      if (response.data.success) {
        const { students } = response?.data?.data || {};
        const normalizedStudents = applyCompletedCoursesToAttendance(
          (students || []).map((student) => ({
            id: student._id,
            isPresent: student.isPresent,
            name: student.name,
            instituteId: student.instituteId,
            addedOn: student.addedOn || null,
            enrolledCourseId: student.enrolledCourseId || null,
            isCompleted: student.isCompleted || false,
            completedAtLectureDay: student.completedAtLectureDay || null,
          }))
        );

        setAttendance(normalizedStudents);
        setCompletedStudents(
          normalizedStudents.filter((student) => student.isCompleted)
        );
      } else {
        setAttendance([]);
        setCompletedStudents([]);
        toast.error('No students found for the selected date.');
      }
    } catch (error) {
      setAttendance([]);
      setCompletedStudents([]);
      toast.error('Failed to fetch students. Please try again.');
    }
  }

  const clearData = () => {
    setAttendance([]);
  };

  const displayStep = (step) => {
    switch (step.stepNo) {
      case 1:
        return (
          <SelectClass
            setLoading={setLoading}
            setDisable={setDisable}
            setClassData={setClassData}
            classData={classData}
            courses={courses}
            loadingCourses={loadingCourses}
            clearData={clearData}
          />
        );
      case 2:
        return (
          <>
            <div className="mb-4 flex gap-4 px-3 md:px-8 pt-4">
              <button
                onClick={() => setAttendanceMode('single')}
                className={`rounded-lg px-6 py-2 font-medium transition ${
                  attendanceMode === 'single'
                    ? 'bg-brand-900 text-white'
                    : 'bg-gray-200 text-gray-700 dark:bg-white/10 dark:text-white'
                }`}
              >
                Single Date
              </button>
              <button
                onClick={() => setAttendanceMode('bulk')}
                className={`rounded-lg px-6 py-2 font-medium transition ${
                  attendanceMode === 'bulk'
                    ? 'bg-brand-900 text-white'
                    : 'bg-gray-200 text-gray-700 dark:bg-white/10 dark:text-white'
                }`}
              >
                Bulk Attendance
              </button>
            </div>
            {attendanceMode === 'single' ? (
              <AttendancesSheet
                getStudents={getStudents}
                setLoading={setLoading}
                setDisable={setDisable}
                classData={classData}
                setClassData={setClassData}
                attendance={attendance}
                handleCheckboxChange={handleCheckboxChange}
                handleMarkAllAttendance={handleMarkAllAttendance}
                getAttendanceMarkAllState={getAttendanceMarkAllState}
                setDate={setDate}
                date={date}
                lectureData={lectureData}
                setLectureData={setLectureData}
                selectedSubtopics={selectedSubtopics}
                setSelectedSubtopics={setSelectedSubtopics}
                completedStudents={completedStudents}
                batchCourses={batchCourses}
                completedCourses={completedCourses}
                setShowCourseCompleteModal={setShowCourseCompleteModal}
              />
            ) : (
              <BulkSheet
                classData={classData}
                minDate={
                  attendanceStartDate || classData.batchStartDate
                    ? new Date(attendanceStartDate || classData.batchStartDate)
                    : undefined
                }
                onSuccess={() => {
                  setAttendance([]);
                  setSelectedSubtopics([]);
                  setLectureData(null);
                  setBatchCourses([]);
                  setCompletedCourses([]);
                  setCompletedStudents([]);
                }}
              />
            )}
          </>
        );
      default:
        return null;
    }
  };

  const confirmCreation = () => {
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (attendance.length === 0) {
      toast.error('No students found');
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const absentStudents = attendance
        .filter((s) => s.isPresent === false && !s.isCompleted)
        .map((s) => s.id);

      const unmarkedStudentIds = attendance
        .filter((s) => s.isPresent === null && !s.isCompleted)
        .map((s) => s.id);

      // Sent explicitly so a Present mark is recorded rather than inferred — on
      // back-dated days an inferred Present cannot be told apart from "unmarked".
      const presentStudentIds = attendance
        .filter((s) => s.isPresent === true && !s.isCompleted)
        .map((s) => s.id);

      const completedStudentIds = attendance
        .filter((s) => s.isCompleted)
        .map((s) => s.id);

      let selectedLectures = [];
      if (
        selectedSubtopics.length > 0 &&
        lectureData &&
        lectureData.length > 0
      ) {
        const lectureMap = {};

        for (let lecture of lectureData) {
          if (!lecture.subtopics) continue;

          const topicsForThisLecture = selectedSubtopics.filter((t) =>
            lecture.subtopics.some((s) => s.title === t)
          );

          if (topicsForThisLecture.length > 0) {
            lectureMap[lecture.lectureDay] = topicsForThisLecture;
          }
        }

        selectedLectures = Object.entries(lectureMap).map(
          ([lectureDay, subtopics]) => ({
            lectureDay: parseInt(lectureDay),
            subtopics,
          })
        );
      }

      const attendancePayload = {
        studentIds: absentStudents,
        unmarkedStudentIds,
        presentStudentIds,
        completedStudents: completedStudentIds,
        courseId: classData.courseId,
        batchId: classData.batchId,
        date: toLocalDateStr(date),
        ...(selectedLectures.length > 0 && { selectedLectures }),
      };

      const res = await attendanceService.submitAttendance(attendancePayload);

      if (!res.data.success) {
        throw new Error(res.data.message || 'Attendance failed');
      }

      toast.success('Attendance saved successfully');
      if (res.data.materialSent === false) {
        toast(res.data.materialMessage || 'Study material was not sent.', {
          icon: '⚠️',
        });
      }

      setCurrentStep(1);
      setClassData({ courseId: '', batchId: '', structureId: '' });
      setAttendance([]);
      setSelectedSubtopics([]);
      setLectureData(null);
      setIsModalOpen(false);
      setBatchCourses([]);
      setCompletedCourses([]);
      setCompletedStudents([]);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (disable) return;
    setLoading(true);
    setCurrentStep(currentStep + 1);
    setLoading(false);
  };

  const handleBack = () => {
    if (disable) return;
    setCurrentStep((cs) => cs - 1);
  };

  const handleKeyDown = (event) => {
    if (disable) return;

    if (event.key === 'Enter') {
      if (currentStep === 1) {
        handleNext();
      }
    } else if (event.key === 'ArrowLeft') {
      if (currentStep > 1) {
        handleBack();
      }
    } else if (event.key === 'ArrowRight') {
      if (currentStep < steps.length) {
        handleNext();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentStep, disable]);

  return (
    <div className="mt-3 h-full w-full">
      <div className="h-[350px] w-full rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600 md:h-[390px] " />
      <div
        className={`w-md:2/3 mx-auto h-full ${
          currentStep === 1 ? 'w-5/6' : 'w-6/6'
        } md:px-3  3xl:w-7/12`}
      >
        <Card
          extra={
            '-mt-[280px] w-full md:-mt-[340px] md:px-[70px] h-full mx-auto pb-3'
          }
        >
          <div className="w-full rounded-[20px]">
            <UseContextProvider>
              {displayStep(steps[currentStep - 1])}
            </UseContextProvider>
          </div>
          {currentStep === 1 ? (
            <div className="flex flex-col justify-end px-3 pt-2 md:flex-row md:px-8">
              <button
                disabled={disable}
                onClick={handleNext}
                className=" linear cursor-pointer rounded-xl bg-brand-900 px-16 py-2 font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90 md:ml-2"
              >
                {loading ? 'Loading..' : 'Next'}
              </button>
            </div>
          ) : (
            <div className="flex flex-col justify-between px-3 pt-2  md:flex-row md:px-8">
              <button
                onClick={handleBack}
                className={`mb-3 cursor-pointer rounded-xl bg-lightPrimary px-16 py-2 font-medium text-navy-700 transition duration-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 dark:active:bg-white/20 md:mb-0`}
              >
                Prev
              </button>
              {attendanceMode === 'single' && (
                <button
                  disabled={disable}
                  onClick={confirmCreation}
                  className={`linear rounded-xl px-16 py-2 font-medium text-white transition duration-200 md:ml-2 ${
                    disable
                      ? 'cursor-not-allowed bg-gray-400'
                      : 'bg-brand-900 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90'
                  }`}
                >
                  Submit
                </button>
              )}
            </div>
          )}
        </Card>
      </div>
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => {
          if (!isSubmitting) {
            setIsModalOpen(false);
          }
        }}
        onConfirm={handleSubmit}
        title="Confirm Attendance Submission"
        message="Are you sure you want to submit the attendance for this class?"
        confirmText={loading ? 'Submitting...' : 'Yes, Submit Attendance'}
        cancelText="Cancel"
        icon="info"
        disableConfirm={isSubmitting}
      />

      <CourseCompleteModal
        isOpen={showCourseCompleteModal}
        onClose={() => setShowCourseCompleteModal(false)}
        batchId={classData.batchId}
        courses={batchCourses}
        completedCourses={completedCourses}
        setCompletedCourses={setCompletedCourses}
      />
    </div>
  );
};

export default MarkAttendance;
